/**
 * AddPayoutMethodModal Component
 *
 * Modal for adding a new payout method.
 */

import React, { useState } from 'react';
import type { PayoutMethod, WhishMoneyDetails, WireTransferDetails } from '../hooks';

interface AddPayoutMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (method: Omit<PayoutMethod, 'id' | 'createdAt'>) => Promise<boolean>;
  existingMethodsCount: number;
}

type MethodType = 'whish_money' | 'wire_transfer';

export const AddPayoutMethodModal: React.FC<AddPayoutMethodModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingMethodsCount,
}) => {
  const [type, setType] = useState<MethodType>('whish_money');
  const [isSaving, setIsSaving] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(existingMethodsCount === 0);

  // Whish Money fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');

  // Wire Transfer fields
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');

  if (!isOpen) return null;

  const isValid = type === 'whish_money'
    ? phoneNumber && accountHolderName
    : bankName && accountNumber && iban && swiftCode && beneficiaryName;

  const handleSave = async () => {
    if (!isValid) return;

    setIsSaving(true);

    const method: Omit<PayoutMethod, 'id' | 'createdAt'> = {
      type,
      name: type === 'whish_money' ? 'Whish Money' : bankName,
      details: type === 'whish_money'
        ? { phoneNumber, accountHolderName } as WhishMoneyDetails
        : { bankName, accountNumber, iban, swiftCode, beneficiaryName } as WireTransferDetails,
      isDefault: setAsDefault || existingMethodsCount === 0,
    };

    const success = await onSave(method);

    if (success) {
      onClose();
      // Reset form
      setPhoneNumber('');
      setAccountHolderName('');
      setBankName('');
      setAccountNumber('');
      setIban('');
      setSwiftCode('');
      setBeneficiaryName('');
    }

    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Add Payout Method</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Type Selection */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setType('whish_money')}
            className={`flex-1 p-4 border rounded-xl text-center transition-colors ${
              type === 'whish_money'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <PhoneIcon className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <span className="block font-medium text-gray-900">Whish Money</span>
            <span className="text-xs text-gray-500">No transfer fee</span>
          </button>
          <button
            onClick={() => setType('wire_transfer')}
            className={`flex-1 p-4 border rounded-xl text-center transition-colors ${
              type === 'wire_transfer'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <BankIcon className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <span className="block font-medium text-gray-900">Wire Transfer</span>
            <span className="text-xs text-gray-500">$15 transfer fee</span>
          </button>
        </div>

        {/* Whish Money Form */}
        {type === 'whish_money' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+961 71 123 456"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder Name *
              </label>
              <input
                type="text"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Wire Transfer Form */}
        {type === 'wire_transfer' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name *
              </label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Bank of Lebanon"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number *
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IBAN *
              </label>
              <input
                type="text"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder="LB62 0999 0000 0001 0019 0000 0001"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SWIFT/BIC Code *
              </label>
              <input
                type="text"
                value={swiftCode}
                onChange={(e) => setSwiftCode(e.target.value)}
                placeholder="BDLLBBBX"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beneficiary Name *
              </label>
              <input
                type="text"
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Set as Default */}
        {existingMethodsCount > 0 && (
          <label className="flex items-center gap-3 mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500 rounded"
            />
            <span className="text-sm text-gray-700">Set as default payout method</span>
          </label>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || isSaving}
            className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Method'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const BankIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

export default AddPayoutMethodModal;
